import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { ContainerForm } from '@/components/Form/ContainerForm'
import { useUser } from '@/context/UserProvider'
import { weekDays } from '@/helpers/constants'
import { typeOfCategories } from '@/helpers/constants'
import { generateMongoObjectId } from '@/helpers/generateMongoObjectId'
import useLeadSubmitTracking from '@/hooks/useLeadSubmitTracking'
import useUTMs from '@/hooks/useUTMs'
import { addNewPatientCentre } from '@/services/patientCentres'
import { postTurn } from '@/services/turns'
import { postcentreConfiguration } from '@/store/centreConfigurations/actions'
import { postCentre } from '@/store/centres/actions'
import { postDoctorCentres } from '@/store/doctorCentres/actions'
import { postMachine } from '@/store/machines/actions'
import { postService } from '@/store/services/actions'
import { convertValueToName } from '@/utils/functions/convertValueToName'
import { toCustomTz } from '@/utils/functions/toCustomTz'
import { yupResolver } from '@hookform/resolvers/yup'

import dayjs from 'dayjs'
import FormStepperButtons from './formSteps/FormStepperButtons'
import StepEmailPassword from './formSteps/StepEmailPassword'
import StepUserDetails from './formSteps/StepUserDetails'
import { formDefaultValues } from './formConfig'
import { formStepTexts } from './formTexts'
import { formValidationSchema } from './formValidation'

import s from './styles.module.scss'
const steps = [StepEmailPassword, StepUserDetails]

// This function creates a work schedule for the next 3 days starting from today
const generateWorkTimes = (serviceId, doctorId) => {
  const workTimes = []
  const today = dayjs().day()

  for (let i = 0; i < 7; i++) {
    const dayCode = (today + i) % 7
    const startTime = dayjs().day(dayCode).hour(8).minute(0).format()
    const finishTime = dayjs().day(dayCode).hour(18).minute(0).format()

    const obj = {
      frequency: { code: 0 },
      onlineTurns: true,
      weekDay: {
        code: dayCode,
        name: convertValueToName(weekDays, dayCode),
      },
      turnDuration: 60,
      startTime,
      finishTime,
      doctorCentres: [doctorId],
      services: [serviceId],
    }

    workTimes.push(obj)
  }

  return workTimes
}

const FormLogin = ({ setIsLoadingCalu }) => {
  const dispatch = useDispatch()
  const { registerNewCentreUser, login, getUser } = useUser()
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [formValues, setFormValues] = useState(formDefaultValues)
  const StepComponent = steps[step]
  const stepText = formStepTexts[step]
  const utms = useUTMs()
  const trackLeadSubmitted = useLeadSubmitTracking()

  const handleSubmit = async (data) => {
    const { firstname, lastname, email, password, mobile, centreName, category, botTrap } = data

    if (botTrap?.length) return
    const translatedCategory = convertValueToName(typeOfCategories, category)

    const setStartHour = dayjs().hour(10).minute(0).second(0).format()
    const setFinishHour = dayjs().hour(11).minute(0).second(0).format()
    const startHour = toCustomTz(setStartHour, 'utc', true, '')
    const finishHour = toCustomTz(setFinishHour, 'utc', true, '')

    const hideAlert = true
    const fakeCentreId = generateMongoObjectId()

    try {
      setLoading(true)
      setIsLoadingCalu(true)
      // 1 - create user with no centre and role 'centre'
      const userPayload = {
        email,
        firstname,
        lastname,
        password,
        mobile,
      }

      await registerNewCentreUser(false, userPayload, null, null, hideAlert)

      // 2- Login
      await login({ email, password })

      //3 - create centre configuration
      const centreConfigurationPayload = {
        centre: fakeCentreId,
        subscriptionAgendasQuantity: '1',
        subscriptionPaymentType: 'bankTransfer',
        enableTurnsPaymentLinkModule: true,
        hasWappModule: true,
        maxWappMessages: '200',
        mercadopagoApplicationFee: '2',
      }
      const createdCentreConfig = await dispatch(postcentreConfiguration(centreConfigurationPayload, hideAlert))

      // 4 - create centre
      const centrePayload = {
        name: centreName,
        category,
        translatedCategory: translatedCategory,
        status: 'demo',
        country: 'ARG',
        centreConfiguration: createdCentreConfig,
        _id: fakeCentreId,
        addToUser: true,
        utms: utms,
        botTrap: data.botTrap,
        hasCashboxModule: true,
        hasPrescriptionsModule: true,
        hasTreatments: false,
        hidePaymentInfo: false,
      }

      const centreCreated = await dispatch(postCentre(centrePayload, hideAlert))
      const selectedEntity = JSON.stringify({ _id: centreCreated._id, role: 'centre' })
      localStorage.setItem('selectedEntity', selectedEntity)

      // 5. force user to update with centre
      await getUser()

      // 6 - create doctor centre, service and machine
      const doctorCentrePayload = {
        centre: centreCreated._id,
        email: 'profesionalDePrueba@calu.app',
        firstname: 'de Prueba',
        lastname: 'Profesional',
      }

      const doctorCentreCreated = await dispatch(postDoctorCentres(doctorCentrePayload, false, hideAlert))

      const servicePayload = {
        centre: centreCreated._id,
        name: 'Consulta de prueba',
        type: 'practice',
        duration: 60,
        price: '1500',
      }

      const serviceCreated = await dispatch(postService(servicePayload, hideAlert))
      const serviceId = serviceCreated?._id
      const doctorId = doctorCentreCreated?._id

      const machinePayload = {
        centre: centreCreated._id,
        name: `Agenda de ${userPayload.firstname}`,
        workTimes: generateWorkTimes(serviceId, doctorId),
      }

      const machineCreated = await dispatch(postMachine(machinePayload, hideAlert))

      const patientPayload = {
        centre: centreCreated._id,
        firstname: firstname,
        lastname: lastname,
        mobile: mobile,
        dni: crypto.randomUUID().replaceAll('-', '').slice(0, 15),
      }

      const patientCreated = await addNewPatientCentre(patientPayload)
      const turnPayload = {
        amount: 0,
        debt: 0,
        centre: centreCreated._id,
        machine: machineCreated._id,
        patientCentre: patientCreated,
        service: serviceCreated,
        startHour: startHour,
        finishHour: finishHour,
      }

      await postTurn(turnPayload)

      trackLeadSubmitted({ event: 'form_submit' })
      navigate(`/agenda/centro/${centreCreated?._id}`)
      setLoading(false)
      setIsLoadingCalu(false)
    } catch (error) {
      console.error('Error al crear el centro:', error)
      setLoading(false)
    }
  }
  return (
    <ContainerForm
      key={step}
      onSubmit={(data) => handleSubmit(data)}
      defaultValues={{ ...formValues }}
      resolver={yupResolver(formValidationSchema[step])}
      mode="onTouched"
      id={'form-cuenta-demo'}
    >
      <div className={s.containerForm}>
        <div className={s.formContainer}>
          <div className={s.title}>{stepText.title}</div>
          {stepText?.secondTitle && <div className={s.secondTitle}>{stepText.secondTitle}</div>}

          {stepText?.subtitle && <p className={s.subtitle}>{stepText.subtitle}</p>}

          <StepComponent fields={stepText.fields} setStep={setStep} />
          <FormStepperButtons
            step={step}
            setStep={setStep}
            stepText={stepText}
            isLastStep={step === steps.length - 1}
            saveValues={(vals) => setFormValues(vals)}
            isLoading={loading}
          />
          {step === 0 && (
            <div className={s.linkContainer}>
              <Link to={'/ingresar'} className={s.tagA}>
                ¿Ya tenés cuenta? Iniciar sesión{' '}
              </Link>
            </div>
          )}
          <p className={s.helpText}>
            <span>¿Necesitas ayuda?</span>{' '}
            <a href="https://wa.link/bf6flo" target="_blank" rel="noopener noreferrer" className={s.tagA}>
              Contactános
            </a>
          </p>
        </div>
      </div>
    </ContainerForm>
  )
}

export default FormLogin
